/**
 * useAssetAliases — React Query hooks for the caller's ticker→asset rules.
 *
 * Mirrors `useNeedsAttention`: the list query owns loading/error state, and the
 * delete mutation invalidates the list so a removed alias disappears without a
 * manual refresh.
 */

import type { TenantAssetAliasRow, ListTenantAssetAliasesResponse } from '@bat/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { defaultQueryRetry } from '@/platform/query-client'
import { extractDisplayableErrorMessage } from '@/shared/utils/displayable-error-message'

import { listAssetAliases, deleteAssetAlias } from '../services/asset-aliases.service'

export const assetAliasKeys = {
  all: ['assetAliases'] as const,
  list: () => ['assetAliases', 'list'] as const,
} as const

export interface UseAssetAliasesListResult {
  aliases: TenantAssetAliasRow[]
  isLoading: boolean
  isError: boolean
  /** Human-readable reason, or null. Never an empty string (see the extractor). */
  error: string | null
  refetch: () => void
}

export function useAssetAliasesList(): UseAssetAliasesListResult {
  const query = useQuery({
    queryKey: assetAliasKeys.list(),
    queryFn: (): Promise<ListTenantAssetAliasesResponse> => listAssetAliases(),
    // These rules change only when the user resolves a ticker or deletes one
    // here, and both paths invalidate explicitly.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: defaultQueryRetry,
  })

  return {
    aliases: query.data?.aliases ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: extractDisplayableErrorMessage(query.error),
    refetch: query.refetch,
  }
}

/**
 * Delete one alias. `retry: false` — a DELETE that already succeeded would
 * answer 404 on the retry and show the user a red error for a removal that
 * actually worked.
 */
export function useDeleteAssetAlias() {
  const queryClient = useQueryClient()

  return useMutation<{ id: string }, Error, string>({
    mutationFn: (aliasId): Promise<{ id: string }> => deleteAssetAlias(aliasId),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetAliasKeys.all })
    },
  })
}
